'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { composeSx } from '../../utils/styledUtils';
import { GlassTooltip } from '../GlassTooltip';

export type InitialsBadgeSize = 'xs' | 'sm' | 'md';

export interface InitialsBadgeProps {
  /** Initials to render. Auto-uppercased. Limited to 3 chars. */
  initials: string;
  /** Optional tooltip with the full name on hover. */
  fullName?: string;
  /** Size token. Default 'sm' (24px) — designed for in-table use. */
  size?: InitialsBadgeSize;
  /**
   * Color seed. If provided, generates a stable accent color from the string so each user gets a
   * consistent monogram color across renders. If omitted, uses muted neutral.
   */
  colorSeed?: string;
  /** Sx overrides. */
  sx?: SxProps<Theme>;
}

const SIZE_MAP: Record<InitialsBadgeSize, { box: number; font: string }> = {
  // Floor of 0.75rem (12px) on all sizes — CLAUDE.md §11b2 accessibility minimum.
  xs: { box: 20, font: '0.75rem' },
  sm: { box: 24, font: '0.75rem' },
  md: { box: 32, font: '0.8125rem' },
};

const ACCENT_KEYS = ['primary', 'secondary', 'success', 'warning', 'error', 'info'] as const;

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function InitialsBadge({ initials, fullName, size = 'sm', colorSeed, sx }: InitialsBadgeProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { box, font } = SIZE_MAP[size];

  const trimmed = initials.trim().slice(0, 3).toUpperCase();

  let bg: string;
  let fg: string;
  if (colorSeed) {
    const accentKey = ACCENT_KEYS[hashString(colorSeed) % ACCENT_KEYS.length];
    const color = theme.palette[accentKey].main;
    bg = alpha(color, isDark ? 0.2 : 0.14);
    fg = isDark ? theme.palette[accentKey].light : theme.palette[accentKey].dark;
  } else {
    bg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    fg = theme.palette.text.secondary;
  }

  const badge = (
    <Box
      aria-label={fullName ?? trimmed}
      sx={composeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: box,
          height: box,
          borderRadius: '50%',
          backgroundColor: bg,
          flexShrink: 0,
        },
        sx,
      )}
    >
      <Typography
        component="span"
        sx={{
          fontSize: font,
          fontWeight: 600,
          color: fg,
          letterSpacing: '0.02em',
          lineHeight: 1,
        }}
      >
        {trimmed}
      </Typography>
    </Box>
  );

  if (fullName) {
    return (
      <GlassTooltip title={fullName} placement="top">
        {badge}
      </GlassTooltip>
    );
  }
  return badge;
}

InitialsBadge.displayName = 'InitialsBadge';

export default InitialsBadge;
